/**
 * Image-relative hotspots for process and hotspots sections. Pin positions
 * and hover boxes are percentages of the JS-sized stage. Media must fill
 * that stage.
 *
 * Desktop: the image is contained, so opening a hotspot does not pan.
 * Process uses one persistent card that slides between numbered markers.
 * Scroll or click selects its step; hover does not replace the active card.
 * Other interactive hotspots open on hover or tap. Open popovers
 * move into [data-process-popover-portal] and are positioned against
 * that box. Process also pins once the section is fully in view and
 * walks each hotspot on scroll, then releases after the last one.
 *
 * Pins and their larger invisible hit boxes open the same popover. Because
 * an open popover moves elsewhere in the page, the document watches where
 * the pointer goes. Leaving through the bottom edge waits briefly so the
 * pointer can reach the hotspot; leaving through any other edge closes at
 * once. This is a small edge-aware delay, not full safe-triangle tracking.
 *
 * Mobile process story: [data-process-story] pins the section once it is
 * fully in view. Further scrolling (and accordion clicks) set the active
 * hotspot and zoom/pan the image so that hotspot sits on the horizontal
 * center. Zoom comes from data-zoom on <process-hotspots>. Process can
 * also pin on desktop (`data-desktop-story`) and walk hotspots on scroll
 * without zoom or pan.
 *
 * Hotspots skips that story. Pins show their content instead of a plus
 * icon on desktop. Below desktop the pins are hidden and the titles
 * are listed at the bottom of the section, each prefixed with +. The
 * section stays h-section and the image is contained in the leftover
 * space above that list, then scaled by data-image-scale. An opt-in mobile
 * card reserves its own space below that image, walks features on scroll or
 * selection, and falls back to the list for reduced motion, short viewports,
 * oversized copy and the theme editor.
 */
(() => {
  const PROCESS_STORY_ZOOM = 1.8;
  const PROCESS_STORY_STEP_VH = 0.85;
  const POPOVER_EXIT_DELAY_MS = 120;

  if (!customElements.get("process-hotspots")) {
    customElements.define(
      "process-hotspots",
      class ProcessHotspots extends HTMLElement {
      connectedCallback() {
        this.stage = this.querySelector("[data-process-stage]");
        this.frame = this.querySelector("[data-process-frame]") || this;
        this.hotspots = Array.from(this.querySelectorAll("[data-hotspot]"));
        this.portal =
          this.closest("[data-process-pin]")?.querySelector(
            "[data-process-popover-portal]",
          ) ||
          this.parentElement?.querySelector("[data-process-popover-portal]");
        this.openHotspot = null;
        this.hoveredHotspot = null;
        this.popoverHotspot = null;
        this.storyRoot = this.closest("[data-process-story]");
        this.activeIndex = -1;
        this.storyHotspot = null;
        this.movingCard = this.portal?.querySelector("[data-process-card]");
        this.cardTitle = this.movingCard?.querySelector("[data-process-card-title]");
        this.cardBody = this.movingCard?.querySelector("[data-process-card-body]");
        this.interactionController = new AbortController();
        const listenerOptions = { signal: this.interactionController.signal };

        this.syncStageSize = this.syncStageSize.bind(this);
        this.repositionOpenPopover = this.repositionOpenPopover.bind(this);
        this.onStoryScroll = this.onStoryScroll.bind(this);
        this.onStoryClick = this.onStoryClick.bind(this);
        this.onDocumentMouseOver = this.onDocumentMouseOver.bind(this);

        this.hotspots.forEach((el) => {
          el._pin = el.querySelector("[data-hotspot-pin]");
          el._hit = el.querySelector("[data-hotspot-hit]");
          el._popover = el.querySelector("[data-hotspot-popover]");
          el._button = el._pin?.querySelector("button");
          el._static = el.hasAttribute("data-hotspot-static");
          if (this.movingCard) {
            el._button?.setAttribute("aria-controls", this.movingCard.id);
            el._button?.classList.add("focus-visible:outline-2", "focus-visible:outline-solid", "focus-visible:outline-offset-4");
          }
          if (!el._static && this.closest(".section-hotspots")) {
            this.promoteStaticHotspot(el);
          }
          if (el._static) return;
          el._button?.addEventListener(
            "click",
            (event) => this.onClick(event, el),
            listenerOptions,
          );
          el._hit?.addEventListener(
            "click",
            (event) => this.onClick(event, el),
            listenerOptions,
          );
          [el._pin, el._hit].forEach((target) => {
            target?.addEventListener(
              "pointerenter",
              (event) => this.onPointerEnter(event, el),
              listenerOptions,
            );
            target?.addEventListener(
              "pointerleave",
              (event) => this.onPointerLeave(event, el),
              listenerOptions,
            );
          });
        });
        document.addEventListener("mouseover", this.onDocumentMouseOver);

        this.media = this.stageMedia();
        this.media?.addEventListener("load", this.syncStageSize);
        this.media?.addEventListener("loadedmetadata", this.syncStageSize);
        this.resizeObserver = new ResizeObserver(() => {
          if (this.featureRoot) this.queueFeatureLayout();
          else this.syncStageSize();
        });
        this.resizeObserver.observe(this);
        if (this.frame && this.frame !== this) {
          this.resizeObserver.observe(this.frame);
        }
        this.addEventListener("scroll", this.repositionOpenPopover);
        window.addEventListener("scroll", this.repositionOpenPopover, true);
        window.addEventListener("resize", this.syncStageSize);
        this.desktopQuery = this.desktopMedia();
        this.desktopQuery?.addEventListener("change", this.syncStageSize);
        this.initStory();
        if (!this.storyRoot) this.hydrateStoryItems();
        this.initMobileCard();
        this.syncStageSize();
        this.initDrag?.();
      }

      disconnectedCallback() {
        this.interactionController?.abort();
        cancelAnimationFrame(this.featureLayoutFrame);
        cancelAnimationFrame(this.featureFrame);
        this.featureResizeObserver?.disconnect();
        this.resetMobileCard();
        this.hotspots.forEach((el) => this.clearHide(el));
        cancelAnimationFrame(this.storyFrame);
        cancelAnimationFrame(this.cardFrame);
        cancelAnimationFrame(this.navigationFrame);
        this.teardownDrag?.();
        this.teardownStory();
        this.resizeObserver?.disconnect();
        document.removeEventListener("mouseover", this.onDocumentMouseOver);
        this.removeEventListener("scroll", this.repositionOpenPopover);
        window.removeEventListener("scroll", this.repositionOpenPopover, true);
        window.removeEventListener("resize", this.syncStageSize);
        this.desktopQuery?.removeEventListener("change", this.syncStageSize);
        if (this.openHotspot) this.unportalPopover(this.openHotspot);
        if (this.hoveredHotspot && this.hoveredHotspot !== this.openHotspot) {
          this.unportalPopover(this.hoveredHotspot);
        }
        this.media?.removeEventListener("load", this.syncStageSize);
        this.media?.removeEventListener("loadedmetadata", this.syncStageSize);
      }

      initMobileCard() {
        this.featureRoot = this.closest("[data-mobile-card-story]");
        if (!this.featureRoot) return;
        this.featureIntro = this.featureRoot.querySelector("[data-process-intro]");
        this.featureDock = this.featureRoot.querySelector("[data-feature-dock]");
        this.featureRail = this.featureDock.querySelector("[data-feature-rail]");
        this.featureCard = this.featureDock.querySelector("[data-feature-card]");
        this.featureTitle = this.featureCard.querySelector("[data-feature-title]");
        this.featureBody = this.featureCard.querySelector("[data-feature-body]");
        this.featureBox = this.featureCard.querySelector("[data-feature-box]");
        this.featureLink = this.featureCard.querySelector("[data-feature-link]");
        this.featureControls = this.featureDock.querySelector("[data-feature-controls]");
        this.featureListContainer = this.featureRoot.querySelector("[data-feature-fallback]");
        this.featureItems = Array.from(this.featureListContainer.querySelector("ul").children);
        this.featureFrame = null;
        this.featureIndex = -1;
        this.featureEntered = false;
        this.featureControls.replaceChildren();
        const options = { signal: this.interactionController.signal };
        this.featureItems.forEach((item, index) => {
          const button = document.createElement("button");
          button.type = "button";
          button.textContent = String(index + 1);
          button.className = "t-body-small";
          button.setAttribute("aria-label", item.querySelector("[data-feature-title]").textContent.trim());
          button.setAttribute("aria-controls", this.featureCard.id);
          button.setAttribute("aria-pressed", "false");
          button.addEventListener("click", () => {
            const top = scrollY + this.featureRoot.getBoundingClientRect().top;
            scrollTo({ top: top + (index + 0.5) * this.featureStepHeight, behavior: "instant" });
            this.updateMobileCard(index, true);
          }, options);
          this.featureControls.append(button);
        });
        this.featureMotionQuery = matchMedia("(prefers-reduced-motion: reduce)");
        this.featureMotionQuery.addEventListener("change", this.syncStageSize, options);
        this.onFeatureScroll = () => {
          if (this.featureFrame) return;
          this.featureFrame = requestAnimationFrame(() => {
            this.featureFrame = null;
            this.syncMobileCardScroll();
          });
        };
        window.addEventListener("scroll", this.onFeatureScroll, { ...options, passive: true });
        this.featureResizeObserver = new ResizeObserver(() => this.queueFeatureLayout());
        this.featureResizeObserver.observe(this.featureIntro);
        document.fonts?.ready.then(() => {
          if (this.isConnected) this.syncStageSize();
        });
      }

      queueFeatureLayout() {
        // Geometry writes can resize the observed image frame. Defer them out
        // of the observer delivery cycle, especially when entering fallback.
        cancelAnimationFrame(this.featureLayoutFrame);
        this.featureLayoutFrame = requestAnimationFrame(this.syncStageSize);
      }

      fillFeatureCard(index) {
        const item = this.featureItems[index];
        this.featureTitle.textContent =
          item.querySelector("[data-feature-title]").textContent;
        this.featureBody.innerHTML =
          item.querySelector("[data-feature-body]").innerHTML;
        const link = item.querySelector("[data-feature-link]");
        this.featureLink.textContent = link?.textContent.trim() ?? "";
        if (link) this.featureBox.href = link.href;
        else this.featureBox.removeAttribute("href");
      }

      resetMobileCard() {
        if (!this.featureRoot) return;
        cancelAnimationFrame(this.featureEntryFrame);
        this.featureRoot.removeAttribute("data-feature-motion");
        this.featureRoot.style.height = "";
        this.featureRoot.style.removeProperty("--feature-viewport");
        this.featureDock.hidden = true;
        this.featureListContainer.hidden = false;
        this.featureEntered = false;
      }

      syncMobileCardLayout() {
        if (!this.featureRoot) return;
        const desktop = this.isDesktop();
        const allowed = !desktop && !this.featureMotionQuery.matches &&
          !window.Shopify?.designMode && this.featureItems.length > 1;
        let fits = false;
        if (allowed) {
          this.featureDock.hidden = false;
          this.featureDock.style.visibility = "hidden";
          let maxHeight = 0;
          this.featureItems.forEach((_, index) => {
            this.fillFeatureCard(index);
            maxHeight = Math.max(maxHeight, this.featureCard.offsetHeight);
          });
          this.fillFeatureCard(Math.max(0, this.featureIndex));
          this.featureRail.style.height = `${maxHeight + 24}px`;
          fits = innerHeight - this.featureIntro.offsetHeight - this.featureDock.offsetHeight >= 200;
          this.featureDock.style.visibility = "";
        }
        const motion = allowed && fits;
        if (!motion) {
          this.resetMobileCard();
          return;
        }
        this.featureRoot.setAttribute("data-feature-motion", "");
        this.featureDock.hidden = false;
        this.featureListContainer.hidden = true;
        this.featureStepHeight = innerHeight * PROCESS_STORY_STEP_VH;
        this.featureRoot.style.setProperty("--feature-viewport", `${innerHeight}px`);
        this.featureRoot.style.height = `${innerHeight + this.featureItems.length * this.featureStepHeight}px`;
        this.syncMobileCardScroll();
      }

      syncMobileCardScroll() {
        if (!this.featureRoot?.hasAttribute("data-feature-motion")) return;
        const travel = -this.featureRoot.getBoundingClientRect().top;
        const index = Math.max(0, Math.min(this.featureItems.length - 1,
          Math.floor(travel / this.featureStepHeight)));
        this.updateMobileCard(index, travel >= 0);
      }

      setFeaturePosition(left, offset = 0, immediate = false) {
        if (immediate) this.featureCard.style.transition = "none";
        this.featureCard.style.transform = `translate3d(${left}px, ${offset}px, 0)`;
        if (immediate) {
          this.featureCard.getBoundingClientRect();
          this.featureCard.style.transition = "";
        }
      }

      updateMobileCard(index, entered) {
        const previousBox = index !== this.featureIndex && this.featureEntered && entered
          ? this.featureCard.getBoundingClientRect() : null;
        if (index !== this.featureIndex) {
          this.fillFeatureCard(index);
          this.featureIndex = index;
          Array.from(this.featureControls.children).forEach((button, i) =>
            button.setAttribute("aria-pressed", String(i === index)));
        }
        const rail = this.featureRail.getBoundingClientRect();
        const button = this.featureControls.children[index].getBoundingClientRect();
        const width = this.featureCard.offsetWidth;
        const clampLeft = (left) => Math.max(0, Math.min(this.featureRail.clientWidth - width, left));
        // Connect the label to its selected number, clamping only at the rail edges.
        const left = clampLeft(button.left + button.width / 2 - rail.left - width / 2);
        if (previousBox) {
          // Preserve the current center when the text changes width. Clamp the
          // starting position too, so a longer label cannot overflow mid-slide.
          const start = clampLeft(previousBox.left + previousBox.width / 2 - rail.left - width / 2);
          this.setFeaturePosition(start, 0, true);
        }
        cancelAnimationFrame(this.featureEntryFrame);
        if (entered && !this.featureEntered) {
          this.setFeaturePosition(left, 24, true);
          this.featureEntryFrame = requestAnimationFrame(() => {
            this.setFeaturePosition(left);
          });
        } else {
          this.setFeaturePosition(left, entered ? 0 : 24);
        }
        this.featureEntered = entered;
      }

      initStory() {
        if (!this.storyRoot) return;
        this.scrollRoot = this.storyRoot.querySelector("[data-process-scroll]");
        this.pin = this.storyRoot.querySelector("[data-process-pin]");
        this.accordion = this.storyRoot.querySelector("[data-process-accordion]");
        this.intro = this.storyRoot.querySelector("[data-process-intro]");
        this.overlayTitle = this.storyRoot.querySelector(
          "[data-process-overlay-title]",
        );
        this.storyTicking = false;
        this.ignoreScroll = false;
        this.accordion?.addEventListener("click", this.onStoryClick);
        this.hydrateStoryItems();
        window.addEventListener("scroll", this.onStoryScroll, {
          passive: true,
        });
      }

      promoteStaticHotspot(el) {
        el._static = true;
        el.setAttribute("data-hotspot-static", "");
        el._button?.classList.add("hidden");
        el._hit?.classList.add("hidden");
        if (!el._pin || !el._popover) return;

        el._pin.append(el._popover);
        el._pin.classList.remove("size-[34px]", "-translate-y-1/2");
        el._pin.classList.add("w-fit", "max-w-[200px]", "-translate-y-full");
        el._popover.classList.remove(
          "hidden",
          "absolute",
          "bottom-full",
          "left-1/2",
          "-translate-x-1/2",
        );
        el._popover.classList.add("flex", "relative");
      }

      hydrateStoryItems() {
        this.hotspots.forEach((el, i) => {
          const label = String(i + 1);
          el.querySelectorAll("[data-hotspot-index], [data-hotspot-hit-index]").forEach(
            (indexEl) => {
              indexEl.textContent = label;
            },
          );
        });

        const list =
          this.accordion?.querySelector("[data-process-list] > div") ||
          this.closest("[data-process-pin]")?.querySelector(
            "[data-process-list] > ul, [data-process-list] > div",
          );
        if (!list) return;

        this.hotspots.forEach((el, i) => {
          const item =
            el.querySelector("[data-process-item-template]")?.content
              .firstElementChild?.cloneNode(true);
          if (!item) return;
          item.dataset.index = String(i);
          if (!list.querySelector(`[data-index="${i}"]`)) list.append(item);
        });
      }

      teardownStory() {
        if (!this.storyRoot) return;
        this.accordion?.removeEventListener("click", this.onStoryClick);
        window.removeEventListener("scroll", this.onStoryScroll);
        this.closeStoryHotspot();
        this.resetStoryLayout();
      }

      isMobileStory() {
        return Boolean(this.storyRoot) && !this.isDesktop();
      }

      isDesktopStory() {
        return (
          Boolean(this.storyRoot?.hasAttribute("data-desktop-story")) &&
          this.isDesktop()
        );
      }

      isPinnedStory() {
        return this.isMobileStory() || this.isDesktopStory();
      }

      mobileImageScale() {
        const value = Number(this.dataset.imageScale);
        return Number.isFinite(value) && value > 0 ? value : 1;
      }

      storyZoom() {
        const value = Number(this.dataset.zoom);
        return Number.isFinite(value) && value > 0 ? value : PROCESS_STORY_ZOOM;
      }

      stageMedia() {
        return Array.from(this.stage?.querySelectorAll("img, video") || []).find(
          (media) => !media.closest("[data-hotspot]"),
        );
      }

      fillStageMedia() {
        this.stage.querySelectorAll("img, video").forEach((media) => {
          if (media.closest("[data-hotspot]")) return;
          media.style.width = "100%";
          media.style.height = "100%";
          media.style.maxWidth = "none";
          media.style.objectFit = "fill";
        });
      }

      syncStageSize() {
        if (!this.stage) return;
        this.syncMobileCardLayout();

        if (this.isMobileStory()) {
          this.closeStoryHotspot();
          this.syncStoryHeight();
          this.syncStoryStep(true);
          this.syncStoryStage();
          return;
        }

        this.resetStoryLayout();
        if (this.isDesktopStory()) this.syncStoryHeight();

        const frameWidth = this.clientWidth;
        const frameHeight = this.clientHeight;
        if (!frameWidth) {
          if (this.isDesktopStory()) this.syncStoryStep();
          return;
        }

        const ratio = this.imageRatio();
        if (!ratio) {
          if (this.isDesktopStory()) this.syncStoryStep();
          return;
        }

        let width;
        let height;

        if (this.isDesktop()) {
          if (!frameHeight) {
            if (this.isDesktopStory()) this.syncStoryStep();
            return;
          }
          width = frameWidth;
          height = width / ratio;
          if (height > frameHeight) {
            height = frameHeight;
            width = height * ratio;
          }
        } else {
          width = frameWidth;
          height = width / ratio;
          if (frameHeight && height > frameHeight) {
            height = frameHeight;
            width = height * ratio;
          }
          const scale = this.mobileImageScale();
          width *= scale;
          height *= scale;
        }

        this.stage.style.width = `${width}px`;
        this.stage.style.height = `${height}px`;
        this.stage.style.maxWidth = `${width}px`;
        this.stage.style.minWidth = "0px";
        this.fillStageMedia();
        if (this.isDesktopStory()) this.syncStoryStep(true);
        this.repositionOpenPopover();
      }

      resetStoryLayout() {
        if (this.scrollRoot && !this.isDesktopStory()) {
          this.scrollRoot.style.height = "";
        }
        if (this.stage) {
          this.stage.style.position = "";
          this.stage.style.left = "";
          this.stage.style.top = "";
          this.stage.style.transform = "";
          this.stage.style.transformOrigin = "";
        }
        if (!this.storyRoot) return;
        this.intro?.removeAttribute("inert");
        this.overlayTitle?.setAttribute("inert", "");
        this.accordion
          ?.querySelector("[data-process-teaser]")
          ?.removeAttribute("inert");
        this.accordion
          ?.querySelector("[data-process-list]")
          ?.setAttribute("inert", "");
        if (this.isDesktop() && !this.isDesktopStory()) {
          this.storyRoot.dataset.processStep = "intro";
        }
      }

      syncStoryHeight() {
        if (!this.scrollRoot) return;
        const steps = this.hotspots.length + 1;
        const vh = this.pin?.offsetHeight || window.innerHeight;
        this.scrollRoot.style.height = `${
          vh + (steps - 1) * vh * PROCESS_STORY_STEP_VH
        }px`;
      }

      onStoryScroll() {
        if (this.ignoreScroll || !this.isPinnedStory()) return;
        if (this.storyTicking) return;
        this.storyTicking = true;
        this.storyFrame = requestAnimationFrame(() => {
          this.storyTicking = false;
          this.syncStoryStep();
          if (this.isMobileStory()) this.syncStoryStage();
        });
      }

      onStoryClick(event) {
        if (!this.isMobileStory()) return;
        const teaser = event.target.closest("[data-process-teaser]");
        if (teaser) {
          event.preventDefault();
          this.goToStoryStep(0);
          return;
        }
        const item = event.target.closest("[data-process-item]");
        if (!item) return;
        event.preventDefault();
        this.goToStoryStep(Number(item.dataset.index));
      }

      goToStoryStep(index) {
        this.setStoryStep(index);
        if (this.isMobileStory()) this.syncStoryStage();
        if (!this.scrollRoot || !this.pin) return;

        const stepCount = this.hotspots.length + 1;
        const range = this.scrollRoot.offsetHeight - this.pin.offsetHeight;
        if (range <= 0 || stepCount <= 1) return;

        const top =
          window.scrollY + this.scrollRoot.getBoundingClientRect().top;
        const y = top + ((index + 1) / (stepCount - 1)) * range;
        this.ignoreScroll = true;
        window.scrollTo({ top: y, behavior: "auto" });
        this.navigationFrame = requestAnimationFrame(() => {
          this.ignoreScroll = false;
        });
      }

      syncStoryStep(force = false) {
        if (!this.isPinnedStory() || !this.scrollRoot || !this.pin) return;

        const range = this.scrollRoot.offsetHeight - this.pin.offsetHeight;
        const top = this.scrollRoot.getBoundingClientRect().top;
        if (range <= 0 || top > 0) {
          this.setStoryStep(-1, force);
          return;
        }

        const progress = Math.min(1, Math.max(0, -top / range));
        const stepCount = this.hotspots.length + 1;
        const step = Math.round(progress * (stepCount - 1));
        this.setStoryStep(step - 1, force);
      }

      setStoryStep(index, force = false) {
        const next = Math.max(-1, Math.min(this.hotspots.length - 1, index));
        if (next === this.activeIndex && !force) return;
        this.activeIndex = next;
        this.storyRoot.dataset.processStep = next < 0 ? "intro" : String(next);
        this.syncStoryChrome();
        if (this.isMobileStory()) {
          this.syncAccordion();
        }
        if (this.isDesktopStory()) this.syncDesktopStoryHotspot();
      }

      closeStoryHotspot() {
        const el = this.storyHotspot;
        this.storyHotspot = null;
        if (this.movingCard) {
          cancelAnimationFrame(this.cardFrame);
          this.movingCard.hidden = true;
          this.movingCard.inert = true;
          this.hotspots.forEach((hotspot) => {
            hotspot._button?.setAttribute("aria-expanded", "false");
          });
          return;
        }
        if (!el || el === this.openHotspot) return;
        this.closePopover(el);
      }

      syncDesktopStoryHotspot() {
        const el =
          this.activeIndex >= 0 ? this.hotspots[this.activeIndex] : null;
        if (this.movingCard) {
          this.syncMovingCard(el);
          return;
        }
        if (this.storyHotspot === el) return;

        const previous = this.storyHotspot;
        this.storyHotspot = el;
        if (previous && previous !== this.openHotspot && previous !== el) {
          this.closePopover(previous);
        }
        if (el) this.showPopover(el);
      }

      syncMovingCard(el) {
        if (!el) {
          this.closeStoryHotspot();
          return;
        }
        const entering = this.movingCard.hidden;
        const changed = this.storyHotspot !== el;
        this.storyHotspot = el;
        if (changed || entering) {
          this.cardTitle.textContent = `${this.activeIndex + 1}. ${el._button.getAttribute("aria-label")}`;
          this.cardBody.innerHTML =
            el._popover.querySelector(".rte")?.innerHTML || el._popover.innerHTML;
          this.hotspots.forEach((hotspot) => {
            hotspot._button?.setAttribute("aria-expanded", String(hotspot === el));
          });
        }
        this.movingCard.hidden = false;
        this.movingCard.inert = false;
        if (entering) {
          // Start just below the first resting position, then rise into place.
          // The same element stays visible through every subsequent step.
          this.movingCard.style.transition = "none";
          this.positionMovingCard(24);
          this.movingCard.getBoundingClientRect();
          this.movingCard.style.transition = "";
          this.cardFrame = requestAnimationFrame(() => this.positionMovingCard());
        } else {
          this.positionMovingCard();
        }
      }

      positionMovingCard(entryOffset = 0) {
        if (!this.storyHotspot || !this.isDesktopStory()) return;
        const marker = this.storyHotspot._button.getBoundingClientRect();
        const portal = this.portal.getBoundingClientRect();
        const width = this.movingCard.offsetWidth;
        const height = this.movingCard.offsetHeight;
        const gap = 12;
        const inset = 16;
        // Keep the card below the fixed navigation, even at the highest pin.
        const minTop = 80;
        const pins = this.hotspots.map((el) => el._button.getBoundingClientRect());
        const centerX = marker.left - portal.left + marker.width / 2 - width / 2;
        const centerY = marker.top - portal.top + marker.height / 2 - height / 2;
        const candidates = [
          [centerX, marker.top - portal.top - height - gap],
          [marker.right - portal.left + gap, centerY],
          [marker.left - portal.left - width - gap, centerY],
          [centerX, marker.bottom - portal.top + gap],
          // Short screens may not have space above the equipment. Keep every
          // numbered control visible by using the clear area below the pins.
          [centerX, Math.max(...pins.map((pin) => pin.bottom)) - portal.top + gap],
        ].map(([x, y]) => [
          Math.max(inset, Math.min(portal.width - width - inset, x)),
          Math.max(minTop, Math.min(portal.height - height - inset, y)),
        ]);
        const [x, y] = candidates.find(([left, top]) => pins.every((pin) =>
          left + width + gap <= pin.left - portal.left ||
          left - gap >= pin.right - portal.left ||
          top + height + gap <= pin.top - portal.top ||
          top - gap >= pin.bottom - portal.top,
        )) || candidates[candidates.length - 1];
        this.movingCard.style.transform = `translate3d(${x}px, ${y + entryOffset}px, 0)`;
      }

      syncStoryChrome() {
        const isIntro = this.activeIndex < 0;
        this.intro?.toggleAttribute("inert", !isIntro);
        this.overlayTitle?.toggleAttribute("inert", isIntro);
        this.accordion
          ?.querySelector("[data-process-teaser]")
          ?.toggleAttribute("inert", !isIntro);
        this.accordion
          ?.querySelector("[data-process-list]")
          ?.toggleAttribute("inert", isIntro);
      }

      syncAccordion() {
        if (!this.accordion) return;

        this.accordion.querySelectorAll("[data-process-item]").forEach((item) => {
          const active = Number(item.dataset.index) === this.activeIndex;
          item.toggleAttribute("data-active", active);
          item.classList.toggle("opacity-40", !active);
          item.classList.toggle("opacity-100", active);
          const body = item.querySelector("[data-process-item-body]");
          body?.classList.toggle("grid-rows-[1fr]", active);
          body?.classList.toggle("grid-rows-[0fr]", !active);
          item
            .querySelector("button")
            ?.setAttribute("aria-expanded", active ? "true" : "false");
        });
      }

      syncStoryStage() {
        if (!this.stage) return;

        const box = this.frame || this;
        const frameWidth = box.clientWidth;
        const frameHeight = box.clientHeight;
        if (!frameWidth || !frameHeight) return;

        const ratio = this.imageRatio();
        if (!ratio) return;

        const width = frameWidth;
        const height = width / ratio;

        this.stage.style.position = "absolute";
        this.stage.style.left = "0px";
        this.stage.style.top = "0px";
        this.stage.style.width = `${width}px`;
        this.stage.style.height = `${height}px`;
        this.stage.style.maxWidth = `${width}px`;
        this.stage.style.minWidth = "0px";
        this.stage.style.transformOrigin = "0 0";
        this.fillStageMedia();

        const scale = this.activeIndex < 0 ? 1 : this.storyZoom();
        const scaledWidth = width * scale;
        const scaledHeight = height * scale;
        let tx = (frameWidth - scaledWidth) / 2;
        const ty = (frameHeight - scaledHeight) / 2;

        if (this.activeIndex >= 0) {
          const x = Number(this.hotspots[this.activeIndex]?.dataset.x) / 100;
          tx = frameWidth / 2 - x * scaledWidth;
        }

        this.stage.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
      }

      desktopMedia() {
        const min = getComputedStyle(document.documentElement)
          .getPropertyValue("--breakpoint-lg")
          .trim();
        return min ? matchMedia(`(min-width: ${min})`) : null;
      }

      isDesktop() {
        return Boolean(this.desktopMedia()?.matches);
      }

      canHover() {
        return matchMedia("(hover: hover) and (pointer: fine)").matches;
      }

      imageRatio() {
        const img = this.stageMedia();
        const holder = this.stage.querySelector("[data-aspect-ratio]");

        if (img?.naturalWidth) return img.naturalWidth / img.naturalHeight;
        if (img?.videoWidth) return img.videoWidth / img.videoHeight;
        if (holder?.dataset.aspectRatio) {
          return Number(holder.dataset.aspectRatio);
        }
        return null;
      }

      onClick(event, el) {
        if (el._static) return;
        if (el.dataset.didDrag === "true") {
          event.preventDefault();
          event.stopPropagation();
          el.dataset.didDrag = "false";
          return;
        }
        if (this.movingCard && this.isDesktopStory()) {
          this.goToStoryStep(this.hotspots.indexOf(el));
          return;
        }
        if (this.canHover()) return;
        this.toggle(el);
      }

      // Pins and their larger hit boxes share the same hover behavior.
      onPointerEnter(event, el) {
        if (this.movingCard) return;
        if (el._static) return;
        if (event.pointerType && event.pointerType !== "mouse") return;
        this.showPopover(el);
      }

      onPointerLeave(event, el) {
        if (this.movingCard) return;
        if (event.pointerType && event.pointerType !== "mouse") return;
        this.hidePopover(el);
      }

      // The popover moves into a portal, so document events detect its edges.
      // Only the bottom edge gets time to reach the hotspot.
      onDocumentMouseOver(event) {
        const entered = this.hotspots.find(
          (el) => !el._static && el._popover?.contains(event.target),
        );
        if (entered) {
          if (entered === this.popoverHotspot) return;
          this.popoverHotspot = entered;
          this.showPopover(entered);
          return;
        }

        const el = this.popoverHotspot;
        if (!el) return;
        if (el._pin?.contains(event.target) || el._hit?.contains(event.target)) {
          this.popoverHotspot = null;
          this.clearHide(el);
          return;
        }

        const leftThroughBottom =
          event.clientY >= el._popover.getBoundingClientRect().bottom;
        if (leftThroughBottom) {
          this.hidePopover(el);
        } else {
          this.closePopover(el);
        }
      }

      setHoverState(el, hovered) {
        if (el === this.openHotspot) return;
        el._button?.setAttribute("aria-expanded", hovered ? "true" : "false");
      }

      closePopover(el) {
        if (el === this.storyHotspot) return;
        this.clearHide(el);
        if (this.openHotspot === el) this.openHotspot = null;
        if (this.hoveredHotspot === el) this.hoveredHotspot = null;
        if (this.popoverHotspot === el) this.popoverHotspot = null;
        el.removeAttribute("data-open");
        el._button?.setAttribute("aria-expanded", "false");
        this.unportalPopover(el);
      }

      showPopover(el) {
        this.clearHide(el);
        if (this.storyHotspot && el !== this.storyHotspot) return;
        if (
          this.hoveredHotspot &&
          this.hoveredHotspot !== el &&
          this.hoveredHotspot !== this.openHotspot
        ) {
          this.closePopover(this.hoveredHotspot);
        }
        this.hoveredHotspot = el;
        this.setHoverState(el, true);
        this.portalPopover(el);
      }

      // This delay prevents flicker while crossing the bottom gap.
      hidePopover(el) {
        if (el === this.openHotspot || el === this.storyHotspot) return;
        this.clearHide(el);
        el._hidePopover = setTimeout(() => {
          el._hidePopover = null;
          if (el === this.openHotspot) return;
          this.closePopover(el);
        }, POPOVER_EXIT_DELAY_MS);
      }

      clearHide(el) {
        if (!el._hidePopover) return;
        clearTimeout(el._hidePopover);
        el._hidePopover = null;
      }

      toggle(el) {
        if (this.openHotspot === el) {
          this.closePopover(el);
          return;
        }
        if (this.openHotspot) this.closePopover(this.openHotspot);
        this.open(el);
        this.openHotspot = el;
      }

      open(el) {
        el._button?.setAttribute("aria-expanded", "true");
        el.setAttribute("data-open", "true");
        this.portalPopover(el);
        this.scrollToHotspot(el);
      }

      portalPopover(el) {
        const popover = el._popover;
        if (!popover || !this.portal) return;

        this.portal.append(popover);
        popover.classList.remove("hidden");
        popover.classList.add("flex", "pointer-events-auto");
        this.positionPopover(el);
      }

      unportalPopover(el) {
        const popover = el._popover;
        if (!popover) return;

        el.append(popover);
        popover.classList.add("hidden");
        popover.classList.remove("flex", "pointer-events-auto");
        popover.style.position = "";
        popover.style.left = "";
        popover.style.top = "";
        popover.style.bottom = "";
        popover.style.margin = "";
        popover.style.zIndex = "";
      }

      positionPopover(el) {
        const popover = el._popover;
        const button = el._button;
        if (!popover || !button || !this.portal) return;

        const rect = button.getBoundingClientRect();
        const portalRect = this.portal.getBoundingClientRect();
        const gap =
          parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue(
              "--space-2",
            ),
          ) || 12;

        popover.style.position = "absolute";
        popover.style.left = `${rect.left - portalRect.left + rect.width / 2}px`;
        popover.style.top = "auto";
        popover.style.bottom = `${portalRect.bottom - rect.top + gap}px`;
        popover.style.margin = "0";
        popover.style.zIndex = "20";
      }

      repositionOpenPopover() {
        if (this.movingCard && !this.movingCard.hidden) this.positionMovingCard();
        if (this.openHotspot) this.positionPopover(this.openHotspot);
        if (this.hoveredHotspot && this.hoveredHotspot !== this.openHotspot) {
          this.positionPopover(this.hoveredHotspot);
        }
      }

      scrollToHotspot(el) {
        if (!this.stage || this.isDesktop() || this.isMobileStory()) return;
        const x = Number(el.dataset.x) / 100;
        const left = this.stage.offsetWidth * x - this.clientWidth / 2;
        this.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
      }
      },
    );
  }
})();
